// ═══════════════════════════════════════════════════════════════════
//  SearchContext — Global search state.
// ═══════════════════════════════════════════════════════════════════
//
//  Provides `{ query, scope, mode, isOpen, recents }` plus dispatchers.
//  Wrapped near the top of the provider stack so the SearchBar in the
//  header and any page can both see the same state.
//
//  The actual search index + execution lives in useSearchIndex /
//  searchEngine (Phase B); this context only carries the query.
//
// ═══════════════════════════════════════════════════════════════════

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { RecentSearch, SearchMode, SearchScope } from '@/types/search';

const RECENTS_KEY = 'fw-search-recents';
const RECENTS_LIMIT = 20;

interface SearchContextValue {
  /** Current query as the user types. May be empty. */
  query: string;
  /** Active scope for the search. Default 'all'. */
  scope: SearchScope;
  /** Current visible mode (idle / live-filter / quick / canvas). */
  mode: SearchMode;
  /** Whether the dropdown / overlay is open. Independent of `mode`. */
  isOpen: boolean;
  /** Last 20 distinct queries the user ran (most-recent first). */
  recents: RecentSearch[];

  setQuery: (q: string) => void;
  setScope: (s: SearchScope) => void;
  setMode: (m: SearchMode) => void;
  setOpen: (open: boolean) => void;
  pushRecent: (entry: Omit<RecentSearch, 'ranAt'>) => void;
  clearRecents: () => void;
  /** Convenience: clear query + close overlay. */
  reset: () => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<SearchScope>('all');
  const [mode, setMode] = useState<SearchMode>('idle');
  const [isOpen, setIsOpen] = useState(false);
  const [recents, setRecents] = useState<RecentSearch[]>(() => loadRecents());

  // Persist recents on change.
  useEffect(() => {
    try {
      localStorage.setItem(RECENTS_KEY, JSON.stringify(recents));
    } catch {
      // localStorage may be unavailable (private mode); silently skip.
    }
  }, [recents]);

  const pushRecent = useCallback((entry: Omit<RecentSearch, 'ranAt'>) => {
    if (!entry.query.trim()) return;
    setRecents((prev) => {
      const filtered = prev.filter(
        (r) => !(r.query === entry.query && r.scope === entry.scope),
      );
      const next: RecentSearch = { ...entry, ranAt: Date.now() };
      return [next, ...filtered].slice(0, RECENTS_LIMIT);
    });
  }, []);

  const clearRecents = useCallback(() => {
    setRecents([]);
  }, []);

  const reset = useCallback(() => {
    setQuery('');
    setIsOpen(false);
    setMode('idle');
  }, []);

  const value = useMemo<SearchContextValue>(
    () => ({
      query,
      scope,
      mode,
      isOpen,
      recents,
      setQuery,
      setScope,
      setMode,
      setOpen: setIsOpen,
      pushRecent,
      clearRecents,
      reset,
    }),
    [query, scope, mode, isOpen, recents, pushRecent, clearRecents, reset],
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

/** Read the search context. Throws outside `<SearchProvider>`. */
export function useSearch(): SearchContextValue {
  const ctx = useContext(SearchContext);
  if (!ctx) {
    throw new Error('useSearch() must be called inside <SearchProvider>');
  }
  return ctx;
}

function loadRecents(): RecentSearch[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentSearch =>
        !!e &&
        typeof e === 'object' &&
        typeof (e as RecentSearch).query === 'string' &&
        typeof (e as RecentSearch).ranAt === 'number',
    );
  } catch {
    return [];
  }
}
